import * as yup from 'yup'

import { JOB_SORT_BY, VALIDATION } from '../constants.js'

import allowedValues from './allowed.values.js'
import { pageNum, pageSize, states, sortDirection, string, requiredInteger, jobId } from '../yup/schemas.js'

import { recruiterSchema } from '../recruiter/schema.js'
import { schoolSchema } from '../school/schema.js'
import { companySchema } from '../company/general.schema.js'

import {
  jobSchema, jobLocationTypes, statuses, positionAll, positionsAll, requiredLicenseTypeAll,
  physicalRequirementsAll, payStructureAll, doublesOrTriplesAll, tankerAll, hazardousMaterialsAll
} from './general.schema.js'
import { createJobGroupSchemas } from './new.job.schema.js'
import { patchJobSchemas, patchJobGroupSchemas } from './patch.job.schema.js'

const getJobParamsSchema = jobSchema.pick(['id'])
const getJobGroupParamsSchema = yup.object({
  id: jobSchema.get('groupId')
}).objectRequired()
const getJobListQueryParamsSchema = yup.object({
  pageNum,
  pageSize,
  groupId: jobSchema.get('groupId'),

  jobLocationType: jobSchema.get('jobLocationType'),
  jobLocationTypes: jobLocationTypes
    .existsUnless('jobLocationType', { defined: false }),

  position: positionAll,
  positions: positionsAll
    .existsUnless('position', { defined: false }),

  drivingStates: states,
  recruiterId: recruiterSchema.get('id'),
  companyId: companySchema.get('id'),
  schoolId: schoolSchema.get('id'),
  jobId: jobSchema.get('id'),

  company: companySchema.get('name'),
  companies: yup.array().of( yup.string() )
    .unique()
    .existsUnless('company', { defined: false }),

  status: jobSchema.get('status'),
  statuses: statuses
    .existsUnless('status', { defined: false }),

  autoRenew: jobSchema.get('autoRenew'),
  jobsBoardType: jobSchema.get('jobsBoardType'),

  createdAfter: yup.date(),
  createdBefore: yup.date(),
  expiresAfter: yup.date(),
  expiresBefore: yup.date(),
  billingStartedAfter: yup.date(),
  billingStartedBefore: yup.date(),

  lastPaymentAfter: yup.date(),
  lastPaymentBefore: yup.date(),

  nearZipCode: jobSchema.get('reportingLocation.zip'),
  nearZipCodeDistance: yup.number().min(1).max(250),

  requiredLicenseType: requiredLicenseTypeAll,

  doublesOrTriples: doublesOrTriplesAll,
  tanker: tankerAll,
  hazardousMaterials: hazardousMaterialsAll,
  manualTransmissionRestriction: jobSchema.get('reportingLocation.endorsements.manualTransmissionRestriction'),

  TWICCardRequired: jobSchema.get('TWICCardRequired'),
  minimumAge: jobSchema.get('minimumAge'),
  yearsOfExperienceNeeded: requiredInteger
    .min(0, VALIDATION.MESSAGES.MIN(0))
    .max(30, VALIDATION.MESSAGES.MAX(30)),

  signOnBonus: jobSchema.get('signOnBonus'),
  tuitionReimbursement: jobSchema.get('tuitionReimbursement'),

  physicalRequirements: physicalRequirementsAll, // it is the same in regional, Team Driving and OTR options
  fullTime: jobSchema.get('localJobOptions').fields.fullTime, // it is the same in regional, Team Driving and OTR options
  payStructure: payStructureAll, // it is the same in regional, Team Driving and OTR options

  dedicatedRoute: jobSchema.get('regionalJobOptions').fields.dedicatedRoute, // it is the same in OTR options

  schoolNameSearch: string,
  citySearch: string,
  typeName: string,
  recruiterNameSearch: string,
  sortBy: yup.string().oneOf(JOB_SORT_BY),
  sortDirection
}).objectRequired().allFieldsOptional()
const getCompanyJobListQueryParamsSchema = getJobListQueryParamsSchema.omit(['companyId'])
const getSchoolJobListQueryParamsSchema = getJobListQueryParamsSchema.omit(['schoolId'])
const getSchoolBoardJobListQueryParams = getJobListQueryParamsSchema.pick([
  'pageNum', 'pageSize', 'drivingStates', 'jobLocationType',
  'jobLocationTypes', 'position', 'positions', 'billingStartedAfter',
  'billingStartedBefore', 'lastPaymentAfter', 'lastPaymentBefore',
  'citySearch', 'nearZipCode', 'nearZipCodeDistance',
  'requiredLicenseType', 'doublesOrTriples',
  'tanker', 'hazardousMaterials', 'manualTransmissionRestriction',
  'TWICCardRequired', 'minimumAge',
  'signOnBonus', 'tuitionReimbursement',
  'physicalRequirements', 'fullTime', 'payStructure',
  'dedicatedRoute', 'yearsOfExperienceNeeded', 'companies', 'company',
  'typeName', 'jobId'
])
const getJobGroupListQueryParamsSchema = yup.object({
  pageNum,
  pageSize,
  recruiterId: recruiterSchema.get('id'),
  schoolId: schoolSchema.get('id'),
  jobLocationType: jobSchema.get('jobLocationType')
}).objectRequired().allFieldsOptional()

const getJobParamsSchemas = { front: getJobParamsSchema, back: getJobParamsSchema }
const getJobGroupParamsSchemas = { front: getJobGroupParamsSchema, back: getJobGroupParamsSchema }
const getCompanyJobListQueryParamsSchemas = { front: getCompanyJobListQueryParamsSchema, back: getCompanyJobListQueryParamsSchema }
const getSchoolJobListQueryParamsSchemas = { front: getSchoolJobListQueryParamsSchema, back: getSchoolJobListQueryParamsSchema }
const getSchoolBoardJobListQueryParamsSchemas = { front: getSchoolBoardJobListQueryParams, back: getSchoolBoardJobListQueryParams }
const getJobListQueryParamsSchemas = { front: getJobListQueryParamsSchema, back: getJobListQueryParamsSchema }
const getJobGroupListQueryParamsSchemas = { front: getJobGroupListQueryParamsSchema, back: getJobGroupListQueryParamsSchema }

export default {
  createJobGroup: createJobGroupSchemas,
  patchJob: patchJobSchemas,
  patchJobGroup: patchJobGroupSchemas,
  getJobParams: getJobParamsSchemas,
  getJobGroupParams: getJobGroupParamsSchemas,
  getJobListQueryParams: getJobListQueryParamsSchemas,
  getCompanyJobListQueryParams: getCompanyJobListQueryParamsSchemas,
  getSchoolJobListQueryParams: getSchoolJobListQueryParamsSchemas,
  getSchoolBoardJobListQueryParams: getSchoolBoardJobListQueryParamsSchemas,
  getJobGroupListQueryParams: getJobGroupListQueryParamsSchemas,
  allowedValues
}
